import { useState, useMemo, useEffect } from 'react';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid2';
import Typography from '@mui/material/Typography';
import {
  GetAllStructuresListForTemplateBuilderQuery,
  useGetAllStructuresListForTemplateBuilderQuery,
} from '@/__generated__/graphql';
import { FALLBACK_INSPECTION_IMAGE } from '@/constants';
import { getCloudfrontUrl } from '@/utils/cloudfront';
import { StructureCardContainer, StructureImg } from './styles';
import { abyssColors } from '@/theme/colors';
import Search from '@mui/icons-material/Search';
import { Box, Autocomplete, TextField, InputAdornment } from '@mui/material';
import { matchSorter } from 'match-sorter';
import { PageLoader } from '../shared/PageLoader';

type StructuresListProps = {
  onPlatformClick: (id: string) => void;
  drawerOpen: boolean;
};

export const StructuresList = ({ onPlatformClick, drawerOpen }: StructuresListProps) => {
  const [searchStructure, setSearchStructure] = useState<string>('');
  const { data: allStructuresData, loading: structuresLoading } =
    useGetAllStructuresListForTemplateBuilderQuery({
      fetchPolicy: 'cache-first',
    });
  const [filteredStructures, setFilteredStructures] = useState<
    GetAllStructuresListForTemplateBuilderQuery['allStructures']
  >([]);

  const structureList = useMemo(() => {
    return allStructuresData?.allStructures ?? [];
  }, [allStructuresData?.allStructures]);

  useEffect(() => {
    if (!searchStructure) setFilteredStructures(structureList);
  }, [searchStructure, structureList]);

  const handleSearchStructure = (searchTerm: string) => {
    setSearchStructure(searchTerm);

    const searchedStructure = structureList.filter(
      (structure) =>
        matchSorter([structure.name], searchTerm, {
          threshold: matchSorter.rankings.WORD_STARTS_WITH,
        }).length > 0
    );

    setFilteredStructures(searchedStructure);
  };

  if (structuresLoading) {
    return (
      <Box sx={{ position: 'relative', top: '25rem' }}>
        <PageLoader />
      </Box>
    );
  }

  return (
    <Container
      sx={{
        display: 'flex',
        flexDirection: 'column',
        overflow: 'auto',
        pt: 2,
        pb: 4,
      }}
    >
      <Box flex={1} sx={{ width: '100%', pt: 2, pb: 2 }}>
        <Autocomplete
          options={[...new Set(structureList.map((structure) => structure.name))]}
          onInputChange={(event, newValue) => handleSearchStructure(newValue ?? '')}
          freeSolo
          forcePopupIcon
          clearOnEscape
          filterOptions={(options, { inputValue }) => matchSorter(options, inputValue)}
          renderInput={(parameters) => (
            <TextField
              {...parameters}
              data-testid="search-structure-input"
              sx={{
                width: { md: '100%', xl: '640px' },
              }}
              size="small"
              value={searchStructure}
              placeholder="Search a structure"
              variant="outlined"
              slotProps={{
                input: {
                  ...parameters.InputProps,
                  disableUnderline: true,
                  startAdornment: (
                    <InputAdornment position="start" sx={{ marginLeft: '8px' }}>
                      <Search />
                    </InputAdornment>
                  ),
                },
              }}
            />
          )}
        />
      </Box>

      <Grid container spacing={drawerOpen ? 2 : 4}>
        {filteredStructures.map((structure) => (
          <Grid size={{ xs: 2, sm: 6, md: 4, lg: 3 }} key={structure.id}>
            {' '}
            <StructureCardContainer onClick={() => onPlatformClick(structure.id)}>
              <StructureImg>
                <img
                  style={{ width: '100%' }}
                  src={
                    structure.coverImagePath
                      ? getCloudfrontUrl(structure.coverImagePath)
                      : FALLBACK_INSPECTION_IMAGE
                  }
                  alt={structure.name}
                  onError={(event) => {
                    const reference = event.currentTarget;
                    reference.src = FALLBACK_INSPECTION_IMAGE;
                  }}
                  crossOrigin="use-credentials"
                />
              </StructureImg>
              <Stack>
                <Tooltip title={<Typography fontSize="1.4rem">{structure.name}</Typography>}>
                  <Typography
                    variant="subtitle1"
                    fontWeight={500}
                    lineHeight={1.3}
                    marginBottom={2.5}
                    sx={{
                      textOverflow: 'ellipsis',
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      textAlign: 'left',
                      color: abyssColors.primary,
                    }}
                  >
                    {structure.name}
                  </Typography>
                </Tooltip>
              </Stack>
            </StructureCardContainer>
          </Grid>
        ))}
      </Grid>
    </Container>
  );
};
